/**
 * InboxItemRow
 *
 * Per-variant renderer for the unified inbox feed. Extracted from `InboxPage`
 * (which was over the ~200-line soft threshold) so the page keeps only its
 * page-level responsibilities — screenKey mint, auth gate, `useBasePage`, the
 * loading/empty gates, and the two UI invocations the hook cannot make.
 *
 * Owns the `switch` over the five `InboxItemType` variants, reproducing the
 * Flutter sliver order (`messages_screen.dart:704-768`):
 *   NotificationsListHeader → notifications → MoreNotificationsWidget →
 *   ChatListHeader → chat rows.
 *
 * `getInboxItemKey` lives here as a sibling export so the parallel `switch` that
 * mints each row's stable key stays colocated with the render `switch` it
 * mirrors.
 */
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { InboxItemType } from '@/domain/enums/inbox/inboxItemType';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { InboxItem } from '../types/inboxItem';
import { NotificationItemContainer } from './notifications/NotificationItemContainer';
import { NotificationItemRouter } from './notifications/NotificationItemRouter';
import { NotificationsListHeaderRow } from './NotificationsListHeaderRow';
import { ChatListHeaderRow } from './ChatListHeaderRow';
import { MoreNotificationsRow } from './MoreNotificationsRow';
import { InboxChatItem } from './InboxChatItem';

export interface InboxItemRowProps {
  item: InboxItem;
  currentUserId: string;
  /**
   * Whole-row notification tap → dispatch to the notification's destination,
   * then mark it read. Flutter `onItemTap: _onNotificationItemTap`
   * (`messages_screen.dart:723`).
   */
  onNotificationItemTap: (notification: AppNotificationEntity) => void;
  /**
   * Leading-avatar tap → user details, no mark-read. Flutter
   * `onUserTap: _onNotificationItemUserTap` (`messages_screen.dart:722`).
   */
  onNotificationUserTap: (userId: string) => void;
  /**
   * Trailing-thumbnail tap → content details with no sheet, no mark-read.
   * Flutter `onItemMediaTap: _onNotificationItemMediaTap`
   * (`messages_screen.dart:724`).
   */
  onNotificationMediaTap: (notification: AppNotificationEntity) => void;
  /** "More updates" expand handler. */
  onMore: () => Promise<void>;
  /** "More updates" collapse handler. */
  onCollapse: () => void;
  /** Chat-row tap → build `ChatUsersInfoEntity` and navigate. */
  onChatTap: (node: ChatMessageNodeEntity) => void;
}

export const InboxItemRow = ({
  item,
  currentUserId,
  onNotificationItemTap,
  onNotificationUserTap,
  onNotificationMediaTap,
  onMore,
  onCollapse,
  onChatTap,
}: InboxItemRowProps) => {
  const responsive = useResponsiveDesign();

  switch (item.type) {
    case InboxItemType.notification:
      return (
        <NotificationItemContainer notification={item.notification}>
          <NotificationItemRouter
            notification={item.notification}
            currentUserId={currentUserId}
            onItemTap={onNotificationItemTap}
            onItemMediaTap={onNotificationMediaTap}
            onUserTap={onNotificationUserTap}
          />
        </NotificationItemContainer>
      );
    case InboxItemType.notificationsHeader:
      return (
        <NotificationsListHeaderRow
          hasMoreItems={item.hasMoreItems}
          hasItems={item.hasItems}
        />
      );
    case InboxItemType.moreNotifications:
      return (
        <MoreNotificationsRow
          hasMoreItems={item.hasMoreItems}
          hasItems={item.hasItems}
          isFetching={item.isFetching}
          canCollapse={item.canCollapse}
          onMore={onMore}
          onCollapse={onCollapse}
        />
      );
    case InboxItemType.chatHeader:
      return (
        <ChatListHeaderRow
          notificationsEmpty={item.notificationsEmpty}
          hasMoreItems={item.hasMoreItems}
          hasItems={item.hasItems}
        />
      );
    case InboxItemType.chatMessage:
      // Per-row chrome (Flutter `Padding(left:16, right:16, top:8, bottom:8)`
      // around each chat row — `messages_screen.dart:752-758`). `InboxChatItem`
      // is a pure renderer that intentionally owns no padding (mirroring how
      // `NotificationItemContainer` owns the notification chrome separately), so
      // the row applies it here.
      return (
        <div
          style={{
            paddingLeft: responsive.spacing.lg,
            paddingRight: responsive.spacing.lg,
            paddingTop: responsive.spacing.sm,
            paddingBottom: responsive.spacing.sm,
          }}
        >
          <InboxChatItem
            node={item.node}
            currentUserId={currentUserId}
            onTap={onChatTap}
          />
        </div>
      );
    default:
      return null;
  }
};

/**
 * Stable per-row key. Lives beside `InboxItemRow` because it mirrors the same
 * per-variant `switch` the renderer does.
 */
export const getInboxItemKey = (item: InboxItem): string => {
  switch (item.type) {
    case InboxItemType.notification:
      return `notification:${item.notification.id ?? item.notification.info.timestamp}`;
    case InboxItemType.notificationsHeader:
      return 'header:notifications';
    case InboxItemType.moreNotifications:
      return 'more:notifications';
    case InboxItemType.chatHeader:
      return 'header:chat';
    case InboxItemType.chatMessage:
      // `ChatMessageNodeEntity` has no `documentId`; the node doc lives at
      // `/message_nodes/{groupChatId}`, so `chatId` IS the doc id and the
      // dedup/key field (Task 3).
      return `chat:${item.node.chatId}`;
    default:
      return 'unknown';
  }
};
